import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { api } from "../lib/api";
import { formatJalaliTs, formatMoney, localized } from "../lib/format";

interface AdminKpis {
  publishedHalls: number;
  hallsAwaitingReview: number;
  confirmedBookings: number;
  openTickets: number;
}

interface PendingHall {
  id: string;
  name: Record<string, string>;
  cityId: string;
  hallType: string;
  capacity?: { total: number };
}

interface PayoutRun {
  orgId: string;
  payoutId: string;
  grossMinor: number;
  commissionMinor: number;
  netMinor: number;
  status?: "pending" | "paid";
}

interface PendingReview {
  id: string;
  hallId: string;
  rating: number;
  text?: string;
  createdAt?: string | { _seconds: number };
}

export function AdminPage() {
  const [kpis, setKpis] = useState<AdminKpis | null>(null);
  const [pending, setPending] = useState<PendingHall[]>([]);
  const [payouts, setPayouts] = useState<PayoutRun[]>([]);
  const [reviews, setReviews] = useState<PendingReview[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [settling, setSettling] = useState(false);

  function load() {
    api<AdminKpis>("/admin/dashboard").then(setKpis).catch((e) => setError(e.message));
    api<{ items: PendingHall[] }>("/admin/halls/pending")
      .then((r) => setPending(r.items))
      .catch(() => setPending([]));
    api<{ items: PendingReview[] }>("/admin/reviews")
      .then((r) => setReviews(r.items))
      .catch(() => setReviews([]));
  }

  useEffect(load, []);

  async function review(hallId: string, decision: "approve" | "reject") {
    setBusyId(hallId);
    try {
      await api(`/admin/halls/${hallId}/review`, { method: "POST", body: { decision } });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  async function runSettlement() {
    setSettling(true);
    setError(null);
    try {
      const r = await api<{ payouts: PayoutRun[] }>("/admin/payouts/run", {
        method: "POST",
        idempotent: true,
      });
      setPayouts(r.payouts.map((p) => ({ ...p, status: "pending" })));
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setSettling(false);
    }
  }

  async function markPaid(payout: PayoutRun) {
    setBusyId(payout.payoutId);
    try {
      await api(`/admin/payouts/${payout.orgId}/${payout.payoutId}/mark-paid`, {
        method: "POST",
        idempotent: true,
      });
      setPayouts((prev) =>
        prev.map((p) => (p.payoutId === payout.payoutId ? { ...p, status: "paid" } : p)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  async function moderate(target: PendingReview, decision: "publish" | "reject") {
    setBusyId(target.id);
    try {
      await api("/admin/reviews/moderate", {
        method: "POST",
        body: { hallId: target.hallId, reviewId: target.id, decision },
      });
      setReviews((prev) => prev.filter((r) => r.id !== target.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  const cards = [
    { label: "تالارهای منتشر شده", value: kpis?.publishedHalls },
    { label: "در انتظار بررسی", value: kpis?.hallsAwaitingReview },
    { label: "رزروهای تأیید شده", value: kpis?.confirmedBookings },
    { label: "تیکت‌های باز", value: kpis?.openTickets },
  ];

  return (
    <>
      <Typography variant="h5" mb={2}>
        مدیریت پلتفرم
      </Typography>
      {error && <Typography color="error">{error}</Typography>}
      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  {card.label}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {card.value ?? "…"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" mt={4} mb={1}>
        تالارهای در انتظار بررسی
      </Typography>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>نام</TableCell>
              <TableCell>شهر</TableCell>
              <TableCell>نوع</TableCell>
              <TableCell>ظرفیت</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((hall) => (
              <TableRow key={hall.id} hover>
                <TableCell>{localized(hall.name)}</TableCell>
                <TableCell>
                  <Chip size="small" label={hall.cityId} />
                </TableCell>
                <TableCell>{hall.hallType}</TableCell>
                <TableCell>{hall.capacity?.total ?? "—"}</TableCell>
                <TableCell align="left">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyId === hall.id}
                      onClick={() => review(hall.id, "approve")}
                    >
                      تأیید و انتشار
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={busyId === hall.id}
                      onClick={() => review(hall.id, "reject")}
                    >
                      رد
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {pending.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    موردی در انتظار بررسی نیست
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={4} mb={1}>
        <Typography variant="h6">تسویه پرداخت‌ها</Typography>
        <Button variant="contained" disabled={settling} onClick={runSettlement}>
          {settling ? "در حال اجرا…" : "اجرای تسویه"}
        </Button>
      </Stack>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>سازمان</TableCell>
              <TableCell>ناخالص</TableCell>
              <TableCell>کمیسیون</TableCell>
              <TableCell>خالص</TableCell>
              <TableCell>وضعیت</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((payout) => (
              <TableRow key={payout.payoutId} hover>
                <TableCell>{payout.orgId}</TableCell>
                <TableCell>{formatMoney(payout.grossMinor)}</TableCell>
                <TableCell>{formatMoney(payout.commissionMinor)}</TableCell>
                <TableCell>{formatMoney(payout.netMinor)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={payout.status === "paid" ? "پرداخت شده" : "در انتظار"}
                    color={payout.status === "paid" ? "success" : "warning"}
                  />
                </TableCell>
                <TableCell align="left">
                  {payout.status !== "paid" && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyId === payout.payoutId}
                      onClick={() => markPaid(payout)}
                    >
                      ثبت پرداخت
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {payouts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    برای مشاهدهٔ پرداخت‌های تسویه‌نشده «اجرای تسویه» را بزنید
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" mt={4} mb={1}>
        نظرات در انتظار تأیید
      </Typography>
      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>تالار</TableCell>
              <TableCell>امتیاز</TableCell>
              <TableCell>متن</TableCell>
              <TableCell>تاریخ</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.hallId}</TableCell>
                <TableCell>
                  <Rating value={item.rating} readOnly size="small" />
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>{item.text || "—"}</TableCell>
                <TableCell>{formatJalaliTs(item.createdAt)}</TableCell>
                <TableCell align="left">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="contained"
                      disabled={busyId === item.id}
                      onClick={() => moderate(item, "publish")}
                    >
                      انتشار
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={busyId === item.id}
                      onClick={() => moderate(item, "reject")}
                    >
                      رد
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {reviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" textAlign="center" py={2}>
                    نظری در انتظار تأیید نیست
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
}
